import React, { type ComponentType, type LazyExoticComponent } from 'react';

export const CHUNK_REFRESH_KEY = 'chunk-refresh';
export const CHUNK_REFRESH_TIME_KEY = 'chunk-refresh-time';
export const RELOAD_COOLDOWN_MS = 15000;

export const getStorageItem = (key: string): string | null => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    if (key === CHUNK_REFRESH_KEY) {
      const lastRefresh = sessionStorage.getItem(CHUNK_REFRESH_TIME_KEY);
      if (lastRefresh) {
        const timeSince = Date.now() - parseInt(lastRefresh, 10);
        if (timeSince < RELOAD_COOLDOWN_MS) {
          return 'true';
        }
      }
    }
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

export const setStorageItem = (key: string, value: string): void => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, value);
      if (key === CHUNK_REFRESH_KEY && value === 'true') {
        sessionStorage.setItem(CHUNK_REFRESH_TIME_KEY, Date.now().toString());
      }
    }
  } catch {
    // Ignore storage quota / private browsing errors
  }
};

export const removeStorageItem = (key: string): void => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(key);
      if (key === CHUNK_REFRESH_KEY) {
        sessionStorage.removeItem(CHUNK_REFRESH_TIME_KEY);
      }
    }
  } catch {
    // Ignore storage errors
  }
};

const isChunkErrorMessage = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('chunkloaderror') ||
    normalized.includes('loading chunk') ||
    normalized.includes('failed to fetch dynamically imported module') ||
    normalized.includes('error loading dynamically imported module') ||
    normalized.includes('importing a module script failed') ||
    normalized.includes('error resolving module specifier') ||
    (normalized.includes('load failed') &&
      (normalized.includes('module') ||
        normalized.includes('chunk') ||
        normalized.includes('script'))) ||
    normalized === 'load failed'
  );
};

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  if (typeof error === 'string') {
    return isChunkErrorMessage(error);
  }
  if (typeof error === 'object') {
    const err = error as { name?: string; message?: string; type?: string };
    if (err.name === 'ChunkLoadError') return true;
    if (err.type === 'vite:preloadError') return true;
    if (typeof err.message === 'string' && isChunkErrorMessage(err.message)) return true;
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any> = ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return React.lazy(async () => {
    const pageAlreadyRefreshed = getStorageItem(CHUNK_REFRESH_KEY) === 'true';

    try {
      const component = await componentImport();
      return component;
    } catch (error) {
      if (!pageAlreadyRefreshed) {
        setStorageItem(CHUNK_REFRESH_KEY, 'true');
        if (typeof window !== 'undefined' && window.location) {
          window.location.reload();
        }
        return new Promise<{ default: T }>(() => {}); // Prevent cascading errors while reloading
      }
      throw error;
    }
  });
}

export default lazyWithRetry;
